import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import wellnessPracticeProgressStore from "../store/wellnessPracticeProgressStore";
import { AnimatePresence, motion } from "framer-motion";
import { HeartPlus, Brain, Smile, Sparkles, Check } from "lucide-react";

export const WELLNESS_PATHS = [
  "/deepbreathe",
  "/deepbreathecopy",
  "/howdoifeel",
  "/deepbreathe2",
  "/seeandhear",
  "/seeandhear2",
  "/deepbreathe3",
  "/actions",
  "/performaction",
  "/congratulations",
];

const STEP_INDEX_BY_PATH = Object.fromEntries(
  WELLNESS_PATHS.map((p, i) => [p, i])
);

const STEP_LABELS = [
  { Icon: Smile, text: "CHECK IN" },
  { Icon: Brain, text: "BE MINDFUL" },
  { Icon: HeartPlus, text: "PRACTICE" },
  { Icon: Sparkles, text: "REFLECT" },
];
const BASE_STEP_LABELS = STEP_LABELS.slice(0, 3);
// `gap-1.5` controls spacing between each line segment and step circle in the main tracker.
const BASE_TRACKER_GRID_CLASS =
  "grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2";
// Keep Reflect pre-collapse spacing identical so circle/line gaps match the regular tracker.
const REFLECT_TRACKER_GRID_CLASS =
  "grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)_auto] items-center gap-2";
// Pause briefly on the completed four-step tracker before collapsing into Reflect.
const REFLECT_COLLAPSE_DELAY_MS = 3000;
// Shared collapse timing keeps the circles, bars, and final rails moving together.
const REFLECT_COLLAPSE_TRANSITION = {
  duration: 0.65,
  ease: [0.22, 1, 0.36, 1],
};
const STEP_COMPLETION_FLASH_MS = 500;
const COMPLETION_FLASH_STEP_BY_PATH = {
  "/deepbreathe2": "step1",
  "/deepbreathe3": "step2",
  "/performaction": "step3",
};
const ACTIVE_LINE_COUNT_BY_PATH = {
  "/deepbreathe": 1,
  "/deepbreathecopy": 1,
  "/howdoifeel": 1,
  "/deepbreathe2": 2,
  "/seeandhear": 2,
  "/seeandhear2": 2,
  "/deepbreathe3": 3,
  "/actions": 3,
  "/performaction": 4,
  "/congratulations": 4,
};
const CURRENT_STEP_BY_PATH = {
  "/howdoifeel": 1,
  "/seeandhear": 2,
  "/actions": 3,
};

function useWellnessProgress() {
  const [lineProgress, setLineProgress] = useState(() => wellnessPracticeProgressStore.getState());
  useEffect(() => {
    return wellnessPracticeProgressStore.subscribe(() => {
      setLineProgress(wellnessPracticeProgressStore.getState());
    });
  }, []);
  return lineProgress;
}

function LineSegment({ progress, collapse = false, style }) {
  return (
    <motion.div
      animate={
        collapse
          ? { opacity: 0, scaleX: 1 }
          : { opacity: 1, scaleX: 1 }
      }
      transition={collapse ? REFLECT_COLLAPSE_TRANSITION : { duration: 0.2 }}
      className="flex origin-center items-center"
      style={style}
    >
      <div className="h-1 w-full flex-1 overflow-hidden rounded-full bg-[#F1F2F5]">
        <div
          className="h-full rounded-full bg-[#10B981] transition-[width] duration-[50ms] ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>
    </motion.div>
  );
}

function ReflectFinalRails({ show, reflectReached }) {
  return (
    <motion.div
      initial={false}
      animate={{ opacity: show ? 1 : 0 }}
      transition={{ duration: 0.25, delay: show ? 0.28 : 0 }}
      // This gap is only for the final centered Reflect rails and check circle.
      className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 sm:gap-3"
      aria-hidden
    >
      <motion.div
        initial={false}
        animate={{ opacity: show ? 1 : 0, scaleX: show ? 1 : 0 }}
        transition={REFLECT_COLLAPSE_TRANSITION}
        className="h-1 flex-1 origin-right overflow-hidden rounded-full bg-[#F1F2F5]"
      >
        <div className="h-full w-full rounded-full bg-[#10B981]" />
      </motion.div>
      <motion.div
        initial={false}
        animate={{ opacity: show ? 1 : 0, scale: show ? 1.08 : 1 }}
        transition={REFLECT_COLLAPSE_TRANSITION}
      >
        <StepCircle reached={reflectReached}>
          <Check className="h-5 w-5" aria-hidden />
        </StepCircle>
      </motion.div>
      <motion.div
        initial={false}
        animate={{ opacity: show ? 1 : 0, scaleX: show ? 1 : 0 }}
        transition={REFLECT_COLLAPSE_TRANSITION}
        className="h-1 flex-1 origin-left overflow-hidden rounded-full bg-[#F1F2F5]"
      >
        <div className="h-full w-full rounded-full bg-[#10B981]" />
      </motion.div>
    </motion.div>
  );
}

function StepCircle({
  reached,
  shouldPulse = false,
  animationKey = "idle",
  children,
  ...motionProps
}) {
  return (
    <motion.div
      key={animationKey}
      initial={shouldPulse ? { scale: 1 } : false}
      animate={shouldPulse ? { scale: [1, 1.5, 1] } : { scale: 1 }}
      transition={shouldPulse ? { duration: 0.5, ease: "easeOut" } : { duration: 0.2 }}
      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors duration-200 sm:h-8 sm:w-8 ${reached ? "bg-[#10B981] text-white" : "bg-[#E9E9E9] text-[#555555]"}`}
      aria-hidden="true"
      {...motionProps}
    >
      {children}
    </motion.div>
  );
}

function StepCircleValue({ showCheck, value }) {
  return (
    <span className="relative flex h-4 w-4 items-center justify-center sm:h-5 sm:w-5">
      <AnimatePresence mode="wait" initial={false}>
        {showCheck ? (
          <motion.span
            key="check"
            initial={{ opacity: 0, scale: 0.88, y: 1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, y: -1 }}
            transition={{ type: "spring", damping: 24, stiffness: 260, mass: 0.5 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <Check className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden />
          </motion.span>
        ) : (
          <motion.span
            key={`value-${value}`}
            initial={{ opacity: 0, scale: 0.92, y: -1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.94, y: 1 }}
            transition={{ type: "spring", damping: 24, stiffness: 260, mass: 0.5 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            {value}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}

function StepLabelsRow({ labels = BASE_STEP_LABELS, reflectOnly = false }) {
  if (reflectOnly) {
    const ReflectIcon = STEP_LABELS[3].Icon;
    return (
      <div className="mt-1.5 flex items-center justify-center">
        <span className="flex items-center justify-center gap-1 text-sm uppercase text-[#555555] sm:text-sm">
          <ReflectIcon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />
          <span className="whitespace-nowrap">REFLECT</span>
        </span>
      </div>
    );
  }

  return (
    <div
      className={`mt-1.5 ${
        labels.length === 4 ? REFLECT_TRACKER_GRID_CLASS : BASE_TRACKER_GRID_CLASS
      }`}
    >
      <div aria-hidden />
      {labels.map(({ Icon, text }, i) => {
        const isReflectLabelInFourStepRow = labels.length === 4 && text === "REFLECT";
        return (
          <span
            key={i}
            className={`row-start-1 flex items-center justify-center gap-1 text-sm uppercase text-[#555555] sm:text-sm ${
              isReflectLabelInFourStepRow ? "mr-2" : ""
            }`}
            style={{ gridColumnStart: (i + 1) * 2 }}
          >
            <Icon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />
            <span className="whitespace-nowrap">{text}</span>
          </span>
        );
      })}
    </div>
  );
}

export default function WellnessProgressTracker() {
  const location = useLocation();
  const lineProgress = useWellnessProgress();
  const reflectTrackerRef = useRef(null);
  const completionFlashTimerRef = useRef(null);
  const [reflectCircleOffsets, setReflectCircleOffsets] = useState({});
  const [flashedCompletedStep, setFlashedCompletedStep] = useState(null);
  const stepIndex = STEP_INDEX_BY_PATH[location.pathname] ?? -1;
  const activeStep = CURRENT_STEP_BY_PATH[location.pathname] ?? 0;
  const isReflectStep = location.pathname === "/congratulations";
  const [isReflectLineCollapsed, setIsReflectLineCollapsed] = useState(false);
  const step1Completed = stepIndex >= WELLNESS_PATHS.indexOf("/deepbreathe2");
  const step2Completed = stepIndex >= WELLNESS_PATHS.indexOf("/deepbreathe3");
  const step3Completed = stepIndex >= WELLNESS_PATHS.indexOf("/performaction");
  const reflectReached = stepIndex >= WELLNESS_PATHS.indexOf("/congratulations");

  useEffect(() => {
    const activeLineCount = ACTIVE_LINE_COUNT_BY_PATH[location.pathname];

    if (!activeLineCount) {
      return;
    }

    wellnessPracticeProgressStore.setState((prev) => ({
      ...prev,
      ...(activeLineCount < 4 ? { line4: 0 } : {}),
      ...(activeLineCount < 3 ? { line3: 0 } : {}),
      ...(activeLineCount < 2 ? { line2: 0 } : {}),
    }));
  }, [location.pathname]);

  useEffect(() => {
    if (!isReflectStep) {
      setIsReflectLineCollapsed(false);
      return;
    }

    setIsReflectLineCollapsed(false);
    // Timer controls when the completed four-step tracker starts collapsing.
    const collapseTimer = setTimeout(() => {
      setIsReflectLineCollapsed(true);
    }, REFLECT_COLLAPSE_DELAY_MS);

    return () => clearTimeout(collapseTimer);
  }, [isReflectStep]);

  useEffect(() => {
    if (!isReflectStep) {
      setReflectCircleOffsets({});
      return undefined;
    }

    let frameId;
    const measureCircleOffsets = () => {
      const tracker = reflectTrackerRef.current;
      if (!tracker) {
        return;
      }

      const trackerCenter = tracker.clientWidth / 2;
      const nextOffsets = {};

      tracker.querySelectorAll("[data-reflect-circle]").forEach((circle) => {
        const circleCenter = circle.offsetLeft + circle.offsetWidth / 2;
        nextOffsets[circle.dataset.reflectCircle] = trackerCenter - circleCenter;
      });

      setReflectCircleOffsets(nextOffsets);
    };

    frameId = requestAnimationFrame(measureCircleOffsets);
    window.addEventListener("resize", measureCircleOffsets);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", measureCircleOffsets);
    };
  }, [isReflectStep]);

  useEffect(() => {
    const newlyCompletedStep = COMPLETION_FLASH_STEP_BY_PATH[location.pathname];
    if (!newlyCompletedStep) {
      setFlashedCompletedStep(null);
      return;
    }

    if (completionFlashTimerRef.current) {
      clearTimeout(completionFlashTimerRef.current);
    }
    setFlashedCompletedStep(newlyCompletedStep);
    completionFlashTimerRef.current = setTimeout(() => {
      setFlashedCompletedStep(null);
      completionFlashTimerRef.current = null;
    }, STEP_COMPLETION_FLASH_MS);
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      if (completionFlashTimerRef.current) {
        clearTimeout(completionFlashTimerRef.current);
      }
    };
  }, []);

  const getReflectCircleAnimation = (circleKey, isFinalCircle = false) => ({
    x: isReflectLineCollapsed ? reflectCircleOffsets[circleKey] ?? 0 : 0,
    opacity: isReflectLineCollapsed ? 0 : 1,
    scale: isReflectLineCollapsed ? (isFinalCircle ? 1.08 : 0.55) : 1,
  });

  if (isReflectStep) {
    return (
      <div className="pt-2 2xl:pb-6 pb-2">
        <div className="relative min-h-[32px]" role="progressbar" aria-label="Wellness practice progress">
          <motion.div
            ref={reflectTrackerRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
            className={REFLECT_TRACKER_GRID_CLASS}
          >
            <LineSegment
              progress={lineProgress.line1}
              collapse={isReflectLineCollapsed}
              style={{ gridColumnStart: 1 }}
            />
            <motion.div
              data-reflect-circle="step1"
              animate={getReflectCircleAnimation("step1")}
              transition={REFLECT_COLLAPSE_TRANSITION}
              style={{ gridColumnStart: 2 }}
            >
              <StepCircle reached={step1Completed}>
                <StepCircleValue showCheck={flashedCompletedStep === "step1"} value={1} />
              </StepCircle>
            </motion.div>
            <LineSegment
              progress={lineProgress.line2}
              collapse={isReflectLineCollapsed}
              style={{ gridColumnStart: 3 }}
            />
            <motion.div
              data-reflect-circle="step2"
              animate={getReflectCircleAnimation("step2")}
              transition={REFLECT_COLLAPSE_TRANSITION}
              style={{ gridColumnStart: 4 }}
            >
              <StepCircle reached={step2Completed}>
                <StepCircleValue showCheck={flashedCompletedStep === "step2"} value={2} />
              </StepCircle>
            </motion.div>
            <LineSegment
              progress={lineProgress.line3}
              collapse={isReflectLineCollapsed}
              style={{ gridColumnStart: 5 }}
            />
            <motion.div
              data-reflect-circle="step3"
              animate={getReflectCircleAnimation("step3")}
              transition={REFLECT_COLLAPSE_TRANSITION}
              style={{ gridColumnStart: 6 }}
            >
              <StepCircle reached={step3Completed}>
                <StepCircleValue showCheck={flashedCompletedStep === "step3"} value={3} />
              </StepCircle>
            </motion.div>
            <LineSegment
              progress={lineProgress.line4}
              collapse={isReflectLineCollapsed}
              style={{ gridColumnStart: 7 }}
            />
            <motion.div
              data-reflect-circle="reflect"
              animate={getReflectCircleAnimation("reflect", true)}
              transition={REFLECT_COLLAPSE_TRANSITION}
              className={!isReflectLineCollapsed ? "mr-2" : ""}
              style={{
                gridColumnStart: 8,
              }}
            >
              <StepCircle reached={reflectReached}>
                <Check className="h-5 w-5" aria-hidden />
              </StepCircle>
            </motion.div>
          </motion.div>
          <ReflectFinalRails
            show={isReflectLineCollapsed}
            reflectReached={reflectReached}
          />
        </div>

        <AnimatePresence initial={false}>
          {!isReflectLineCollapsed ? (
            <motion.div
              key="reflect-full-labels"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <StepLabelsRow labels={STEP_LABELS} />
            </motion.div>
          ) : (
            <motion.div
              key="reflect-only-label"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
            >
              <StepLabelsRow reflectOnly />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="pt-2 sm:pb-6 pb-2">
      <div
        className={BASE_TRACKER_GRID_CLASS}
        role="progressbar"
        aria-label="Wellness practice progress"
      >
        <LineSegment progress={lineProgress.line1} />
        <StepCircle
          reached={step1Completed}
          shouldPulse={activeStep === 1}
          animationKey={`step-1-${location.pathname}`}
        >
          <StepCircleValue showCheck={flashedCompletedStep === "step1"} value={1} />
        </StepCircle>
        <LineSegment progress={lineProgress.line2} />
        <StepCircle
          reached={step2Completed}
          shouldPulse={activeStep === 2}
          animationKey={`step-2-${location.pathname}`}
        >
          <StepCircleValue showCheck={flashedCompletedStep === "step2"} value={2} />
        </StepCircle>
        <LineSegment progress={lineProgress.line3} />
        <StepCircle
          reached={step3Completed}
          shouldPulse={activeStep === 3}
          animationKey={`step-3-${location.pathname}`}
        >
          <StepCircleValue showCheck={flashedCompletedStep === "step3"} value={3} />
        </StepCircle>
        <LineSegment progress={lineProgress.line4} />
      </div>

      <StepLabelsRow />
    </div>
  );
}
